import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styles from "./ProductInfoModal.module.css";
import Tooltip from "../../../../components/Tooltip/Tooltip";
import { BundleBuilderConfigInterface } from "../../Types";
import { hexToHsla } from "../../../../utilities/parsers";
import { getWeightUnit, showWeightInfo } from "../../constants";
import { InspectorEditableBlock } from "../../CommonFields/InspectorEditableBlock";
import {
    MediaNode,
    getMediaKey,
    getMediaThumbnailUrl,
    MediaViewer,
    getYouTubeThumbnail,
} from "../../CommonFields/BundleMedia";
import ImageSlider from "../../../../components/Slider/ImageSlider";
import { getProductId } from "../../../constants";

type Props_ProductInfoModal = {
    product: any;
    showProductInfoModal: any;
    onClose: () => void;
    renderVariantSelector: any;
    renderQuantitySelector: any;
    renderPriceArea: any;
    selectedVariants: any;
    bundleConfig: BundleBuilderConfigInterface;
    standAlone?: boolean;
};

const ProductInfoModal = ({
    product,
    showProductInfoModal,
    onClose,
    renderVariantSelector,
    renderQuantitySelector,
    renderPriceArea,
    selectedVariants,
    bundleConfig,
    standAlone = false,
}: Props_ProductInfoModal) => {
    const isFirstVariantChange = useRef(true);

    useEffect(() => {
        if (showProductInfoModal && window.innerWidth <= 840) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }

        return () => {
            document.body.style.overflow = "";
        };
    }, [showProductInfoModal]);

    // Build unified media list — prefer product.media if present, else fall back to images
    const buildMediaList = (): MediaNode[] => {
        if (product.media && product.media.length > 0) {
            return product.media as MediaNode[];
        }
        const productImages = [
            ...(product.images || []),
            ...product.variants.map((v: any) => v.image).filter((img: any) => img?.originalSrc),
        ];

        const unique = Array.from(new Map(productImages.map((img) => [img.originalSrc, img])).values());
        return unique.map((img) => ({
            mediaContentType: "IMAGE" as const,
            alt: img.altText || "",
            image: { url: img.originalSrc, altText: img.altText || "" },
        }));
    };

    const mediaList = buildMediaList();

    const getInitialActiveKey = (sv: any): string => {
        if (sv?.length >= 1 && sv[0]?.image?.originalSrc) {
            return sv[0].image.originalSrc;
        }
        return getMediaKey(mediaList[0]);
    };

    const [activeKey, setActiveKey] = useState<string>(() => getInitialActiveKey(selectedVariants));

    const [expanded, setExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef(null);

    useEffect(() => {
        if (!textRef.current) return;

        const el = textRef.current;

        let rafId: number;
        let timeoutId: NodeJS.Timeout;
        const checkOverflow = () => {
            //@ts-ignore
            const overflowing = el.scrollHeight > el.clientHeight + 10;
            setIsOverflowing(overflowing);
        };

        // Run once after render (slight delay for DOM + fonts)
        timeoutId = setTimeout(() => {
            checkOverflow();
            // Double-check on next frame (handles late layout shifts)
            rafId = requestAnimationFrame(checkOverflow);
        }, 50);

        window.addEventListener("resize", checkOverflow);

        return () => {
            clearTimeout(timeoutId);
            cancelAnimationFrame(rafId);
            window.removeEventListener("resize", checkOverflow);
        };
    }, [product?.descriptionHtml]);

    useEffect(() => {
        if (isFirstVariantChange.current) {
            isFirstVariantChange.current = false;
            return;
        }
        if (selectedVariants?.length >= 1 && selectedVariants[0]?.image?.originalSrc) {
            setActiveKey(selectedVariants[0].image.originalSrc);
        } else if (mediaList.length > 0) {
            setActiveKey(getMediaKey(mediaList[0]));
        }
    }, [selectedVariants]);

    const activeNode = mediaList.find((n) => getMediaKey(n) === activeKey) ?? mediaList[0] ?? null;

    const renderMediaThumbnail = (node: MediaNode, idx: number, activeKey: string) => {
        const key = getMediaKey(node);
        const thumbUrl = getMediaThumbnailUrl(node);
        const isActive = key === activeKey;

        // IMAGE thumbnail
        if (node.mediaContentType === "IMAGE") {
            const variant = product?.variants?.find((v: any) => v.image?.originalSrc === node.image?.url);
            return (
                <Tooltip text={variant?.title || product?.title || "Product"} key={idx}>
                    <img
                        src={node.image?.url}
                        alt={`Thumbnail ${idx}`}
                        className={`${styles.thumbnail} ${isActive ? styles.activeThumbnail : ""}`}
                        style={{
                            border: isActive ? "2px solid #000" : "1px solid #ccc",
                        }}
                    />
                </Tooltip>
            );
        }

        // VIDEO thumbnail
        if (node.mediaContentType === "VIDEO") {
            return (
                <Tooltip text={node.alt || "Video"} key={idx}>
                    <div
                        className={`${styles.thumbnail} ${isActive ? styles.activeThumbnail : ""}`}
                        style={{
                            border: isActive ? "2px solid #000" : "1px solid #ccc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#111",
                            cursor: "pointer",
                            fontSize: 20,
                            color: "#fff",
                            position: "relative",
                            overflow: "hidden",
                        }}
                    >
                        {thumbUrl ? (
                            <video
                                src={thumbUrl}
                                muted
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                }}
                            />
                        ) : (
                            "▶"
                        )}
                        <span
                            style={{
                                position: "absolute",
                                fontSize: 18,
                                color: "#fff",
                                textShadow: "0 0 4px #000",
                            }}
                        >
                            ▶
                        </span>
                    </div>
                </Tooltip>
            );
        }

        // MODEL_3D thumbnail
        if (node.mediaContentType === "MODEL_3D") {
            return (
                <Tooltip text={node.alt || "3D Model"} key={idx}>
                    <div
                        className={`${styles.thumbnail} ${isActive ? styles.activeThumbnail : ""}`}
                        style={{
                            border: isActive ? "2px solid #000" : "1px solid #ccc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#f5f5f5",
                            cursor: "pointer",
                            fontSize: 20,
                        }}
                    >
                        🧊
                    </div>
                </Tooltip>
            );
        }

        // EXTERNAL_VIDEO thumbnail
        if (node.mediaContentType === "EXTERNAL_VIDEO") {
            const ytThumb = getYouTubeThumbnail(node.embeddedUrl);
            return (
                <Tooltip text={node.alt || "Video"} key={idx}>
                    <div
                        className={`${styles.thumbnail} ${isActive ? styles.activeThumbnail : ""}`}
                        style={{
                            border: isActive ? "2px solid #000" : "1px solid #ccc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#111",
                            cursor: "pointer",
                            position: "relative",
                            overflow: "hidden",
                        }}
                    >
                        {ytThumb ? (
                            <img
                                src={ytThumb}
                                alt={node.alt || "Video thumbnail"}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                }}
                            />
                        ) : (
                            <span style={{ fontSize: 20 }}>🎬</span>
                        )}
                        <span
                            style={{
                                position: "absolute",
                                fontSize: 18,
                                color: "#fff",
                                textShadow: "0 0 6px #000",
                            }}
                        >
                            ▶
                        </span>
                    </div>
                </Tooltip>
            );
        }

        return null;
    };

    const CloseIconColor = hexToHsla(bundleConfig?.bundles_design?.popup_bg_color || "#303030");

    return ReactDOM.createPortal(
        <div
            className={styles.overlay}
            onClick={onClose}
            data-form-id={bundleConfig?.form_id}
            data-product-id={getProductId(product.id)}
        >
            <div
                className={styles.modal}
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: bundleConfig?.bundles_design?.popup_bg_color,
                }}
            >
                <button
                    className={styles.closeBtn}
                    onClick={onClose}
                    style={{ color: CloseIconColor?.brightness < 0.5 ? "white" : "black" }}
                >
                    ✖
                </button>

                <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="imagePopup">
                    <div
                        className={styles.mainContainer}
                        style={{
                            color: bundleConfig?.bundles_design?.popup_bg_color,
                        }}
                    >
                        {/* Left side for images */}
                        <div className={styles.imageGalleryWrapper}>
                            <div>
                                <MediaViewer
                                    node={activeNode}
                                    productTitle={product.title}
                                    mainImageClassName={styles.mainImage}
                                />
                            </div>

                            {mediaList.length > 1 && (
                                <ImageSlider
                                    items={mediaList}
                                    activeKey={activeKey}
                                    onItemClick={(node) => setActiveKey(getMediaKey(node))}
                                    renderItem={(node, idx, activeKey) => renderMediaThumbnail(node, idx, activeKey)}
                                />
                            )}
                        </div>

                        {/* Right side for content */}
                        <div className={styles.mainContent}>
                            <h1
                                className={styles.productTitle}
                                style={{
                                    color: bundleConfig?.bundles_design?.popup_text_color || "#303030",
                                }}
                            >
                                {product?.title}
                            </h1>
                            {showWeightInfo(standAlone) &&
                                ((selectedVariants[0]?.weight > 0 && selectedVariants[0]?.weight_unit) ||
                                    product?.metafield2?.value) && (
                                    <span
                                        style={{
                                            display: "flex",
                                            gap: 2,
                                            justifyContent: "flex-start",
                                            alignItems: "flex-start",
                                            fontSize: bundleConfig?.bundles_design.product_name_size - 2 + "px",
                                            fontWeight:
                                                +bundleConfig?.bundles_design?.product_name_style?.split(" ")[0] - 100,
                                            lineHeight: "normal",
                                            color: bundleConfig?.bundles_design?.popup_text_color || "#303030",
                                        }}
                                    >
                                        Weight:{" "}
                                        <span
                                            style={{
                                                display: "flex",
                                                justifyContent: "flex-start",
                                                alignItems: "center",
                                                flexWrap: "nowrap",
                                                gap: 2,
                                            }}
                                        >
                                            {product?.metafield2?.value ? (
                                                <> {product?.metafield2?.value} </>
                                            ) : (
                                                <>
                                                    {selectedVariants[0].weight}
                                                    {getWeightUnit(selectedVariants[0]?.weight_unit || "")}
                                                </>
                                            )}
                                        </span>
                                    </span>
                                )}
                            {product?.descriptionHtml && (
                                <div className={styles.productDesc}>
                                    <p
                                        ref={textRef}
                                        className={`${styles.descContent} ${expanded ? styles.expanded : ""}`}
                                        style={{
                                            color: bundleConfig?.bundles_design?.popup_text_color || "#303030",
                                        }}
                                        dangerouslySetInnerHTML={{ __html: product?.descriptionHtml }}
                                    />
                                    {isOverflowing && (
                                        <span className={styles.readMoreBtn} onClick={() => setExpanded(!expanded)}>
                                            {expanded
                                                ? bundleConfig?.show_less_text || "Show less"
                                                : bundleConfig?.show_more_text || "Show more"}
                                        </span>
                                    )}
                                </div>
                            )}

                            <div className={styles.variantAndQuantitySelector}>
                                {renderPriceArea}
                                {product.variants.length > 1 && renderVariantSelector}
                                {renderQuantitySelector}
                            </div>
                        </div>
                    </div>
                </InspectorEditableBlock>
            </div>
        </div>,
        document.body,
    );
};

export default ProductInfoModal;
